import { Injectable } from '@angular/core';
import { CommonService } from '@app/shared/services/common/common.service';
import { ImageTrimService } from '@app/shared/services/imageTrim/image-trim.service';
import { PdfThumbnailService } from '@app/shared/services/pdfThumbnail/pdf-thumbnail.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { environment } from '@env/environment';
import { Frame, fitFrameToAspect } from './frame-fit';

// Same cap as upload-design.component.ts's MAX_UPLOAD_BASE64_LENGTH — the server's upload size
// limit, checked against the base64 STRING (before decoding), same reasoning as there.
const MAX_UPLOAD_BASE64_LENGTH = 34_000_000;

// How long applyToEditor waits for the SDK's getProjectData before falling back to a url-only
// update (which the designer's export guard then catches). DH answers in well under a second.
const PROJECT_DATA_TIMEOUT_MS = 3000;

/** A processed upload: the URL/data-URI to place, plus the artwork's intrinsic aspect (w/h). */
export interface ProcessedUpload {
  url: string;
  aspect: number | null;
}

/**
 * Overrides DesignHuddle's own "Upload" button inside the live editor so uploads made FROM
 * INSIDE the editor go through the same transparent-padding trim pipeline as the standalone
 * upload-design page, instead of DH's own (untrimmed) file dialog.
 *
 * WHY: per DesignHuddle support, DH never trims anything — it sizes an uploaded asset against
 * its own file dimensions (full PNG canvas / SVG viewBox) when fitting/masking it into a zone.
 * A PNG or SVG exported with blank padding around the real mark renders smaller and offset
 * inside the print zone, for exactly the same reason the upload-design page's own trim step
 * exists (see ImageTrimService). DH has no built-in auto-trim, so the only way to cover
 * in-editor uploads is to intercept the click via `editor.handleMediaUploadRequest(callback)`,
 * run our own file picker + trim pipeline, and place the result via addElement/updateElements —
 * confirmed with DesignHuddle support (officially supported, documented method).
 *
 * Wire this up once per editor instance, right after DSHDLib.Editors.insert's callback delivers
 * the instance:
 *   this.editorInstance = instance;
 *   this.uploadOverride.wireMediaUploadOverride(instance);
 *
 * IMPORTANT CAVEATS (per DesignHuddle support):
 * - This only covers the Upload BUTTON. A user can still drag a file straight onto the canvas
 *   and skip this pipeline entirely unless DH's media drag/drop is disabled for the account —
 *   that's an account-level setting only DH support can turn on, not something this code can do.
 * - addElement/updateElements need a public/signed URL for raster images (PNG/JPG/etc.) — the
 *   trimmed result is uploaded to Azure via the SAME endpoint the upload-design page uses
 *   (CommonService.uploadHuddleLogo), so the resulting `environment.huddleLogoFolder` domain
 *   must be on DesignHuddle's CSP allowed-origin list or the placed image will fail to load.
 *   SVGs skip hosting entirely and are placed as a base64 data URI instead (DH explicitly
 *   supports this for vector elements).
 */
@Injectable({
  providedIn: 'root'
})
export class DesignHuddleUploadOverrideService {

  constructor(
    private commonService: CommonService,
    private imageTrim: ImageTrimService,
    private pdfThumbnail: PdfThumbnailService,
    private notification: NotificationService
  ) { }

  /**
   * Subscribe to the given editor instance's Upload-button clicks. Safe to call once per
   * instance; DSHDLib documents this as replacing DH's own upload flow entirely from then on.
   */
  wireMediaUploadOverride(editor: any): void {
    if (!editor || typeof editor.handleMediaUploadRequest !== 'function') {
      console.warn('[DesignHuddleUploadOverride] editor instance has no handleMediaUploadRequest — skipping override');
      return;
    }

    editor.handleMediaUploadRequest((editorRef: any, data: any) => {
      this.openPickerAndHandle(editorRef || editor, data || {});
    });
  }

  private openPickerAndHandle(editor: any, data: { context?: string; allowed_file_types?: string[]; element_info?: any }): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.style.display = 'none';
    const types = data.allowed_file_types || ['png', 'jpg', 'jpeg', 'svg'];
    input.accept = types.map(t => '.' + t).join(',');
    document.body.appendChild(input);

    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      document.body.removeChild(input);
      if (!file) return;

      this.processFile(file)
        .then(processed => {
          if (processed?.url) return this.applyToEditor(editor, data, processed.url, processed.aspect);
        })
        .catch(err => {
          console.error('[DesignHuddleUploadOverride] Upload/trim failed:', err);
          this.notification.error('Could not process that file. Please try again.', 'Upload failed');
        });
    });

    input.click();
  }

  /**
   * Trim (where applicable) and upload a file via the same pipeline as the in-editor upload-button
   * override, but ALWAYS returns a hosted Azure URL — never an inline data URI. For callers that
   * need a URL a server can fetch (e.g. seeding a new DesignHuddle project's media via
   * createProject), unlike processFile()/the live in-editor addElement call below, which can place
   * an SVG data URI directly since that's a client-side canvas operation.
   */
  async processAndUploadFile(file: File): Promise<string | null> {
    const extension = (file.name.split('.').pop() || '').toLowerCase();

    if (extension === 'pdf') {
      // Same fallback ladder as upload-design.component.ts's resolveUploadPayload — re-render
      // smaller if the PNG would breach the server's upload cap. A vector logo never will at
      // 300 DPI; a photographic full-page PDF can.
      let dataUrl: string | null = null;
      for (const maxEdge of [4000, 2400, 1400]) {
        dataUrl = await this.pdfThumbnail.rasterizePdfFile(file, 300, maxEdge);
        if (!dataUrl || dataUrl.length <= MAX_UPLOAD_BASE64_LENGTH) { break; }
      }
      if (!dataUrl) return null;

      // Margins well beyond the mark itself are common on a logo PDF page; trim them off the
      // canvas rather than leaving them baked into the stored artwork's dimensions.
      dataUrl = await this.imageTrim.trimTransparentPadding(dataUrl);
      return this.uploadTrimmedImage(dataUrl.split(',')[1], 'png');
    }

    if (extension === 'svg') {
      const svgText = await file.text();
      const trimmed = await this.imageTrim.trimSvgPadding(svgText);
      return this.uploadTrimmedImage(this.utf8ToBase64(trimmed), extension);
    }

    let dataUrl = await this.readFileAsDataUrl(file);
    if (extension === 'png') {
      dataUrl = await this.imageTrim.trimTransparentPadding(dataUrl);
    }

    const base64 = dataUrl.split(',')[1];
    return this.uploadTrimmedImage(base64, extension);
  }

  /**
   * Trim (where applicable) and resolve a URL usable by addElement/updateElements, together with
   * the artwork's intrinsic aspect ratio — applyToEditor needs it to re-fit an existing element's
   * frame (see frame-fit.ts). `aspect` is null when the dimensions could not be read.
   */
  private async processFile(file: File): Promise<ProcessedUpload | null> {
    const extension = (file.name.split('.').pop() || '').toLowerCase();

    if (extension === 'svg') {
      const svgText = await file.text();
      const trimmed = await this.imageTrim.trimSvgPaddingWithDims(svgText);
      const svg = trimmed ? trimmed.svg : svgText;
      const aspect = trimmed && trimmed.width > 0 && trimmed.height > 0 ? trimmed.width / trimmed.height : null;
      // SVGs can be placed as a base64 data URI directly — no hosting needed (per DH support).
      return { url: `data:image/svg+xml;base64,${this.utf8ToBase64(svg)}`, aspect };
    }

    let dataUrl = await this.readFileAsDataUrl(file);
    // PNGs are frequently exported on a larger transparent artboard than the visible mark.
    // JPG/webp/etc. have no alpha channel, so trimming only does real work for PNG.
    if (extension === 'png') {
      dataUrl = await this.imageTrim.trimTransparentPadding(dataUrl);
    }

    const dims = await this.imageTrim.getDataUrlDims(dataUrl);
    const aspect = dims && dims.width > 0 && dims.height > 0 ? dims.width / dims.height : null;

    const base64 = dataUrl.split(',')[1];
    const url = await this.uploadTrimmedImage(base64, extension);
    return url ? { url, aspect } : null;
  }

  private uploadTrimmedImage(base64: string, extension: string): Promise<string | null> {
    return new Promise((resolve, reject) => {
      this.commonService.uploadHuddleLogo({ File: base64, Extension: extension }).subscribe({
        next: (response: any) => {
          if (response?.StatusCode === 200 && response?.Data?.ResponseData) {
            resolve(environment.huddleLogoFolder + response.Data.ResponseData);
          } else {
            reject(new Error(response?.Message || 'Upload returned no file name'));
          }
        },
        error: (err: any) => reject(err)
      });
    });
  }

  /**
   * Place the processed image on the canvas. `context: "element"` means the Upload button
   * belonged to an existing element (e.g. the seeded logo placeholder) — update it in place so
   * its position stays where the designer put it. Anything else (`"background"`/`"root"`, or a
   * missing element id) adds a brand-new element instead.
   *
   * IMPORTANT — the frame MUST be re-fitted when replacing media. DH renders a vector element by
   * filling its frame, so sending `{ url }` alone keeps the old frame (the template's square
   * placeholder, or the previous logo's shape) and stretches the new artwork into it: an oval logo
   * in a 2×2in placeholder exported as a circle (order S00327511, 468 print files across 34
   * stores). The thumbnail is unaffected because it comes from a separate createProject-seeded
   * project where DH fits the frame itself. So: read the element's current frame through the SDK,
   * keep its width and centre, derive the height from the artwork's aspect, and send url + frame
   * in one update. If the frame cannot be read or the aspect is unknown, fall back to url-only
   * (the designer's export-time guard then blocks a distorted side from being saved).
   */
  private async applyToEditor(editor: any, data: { context?: string; element_info?: any }, url: string, aspect: number | null = null): Promise<void> {
    const elementId = data?.element_info?.element_id || data?.element_info?.id || null;

    if (data?.context === 'element' && elementId) {
      const payload: any = { url };

      const placement = await this.readElementFrame(editor, elementId);
      const hasAspect = typeof aspect === 'number' && Number.isFinite(aspect) && aspect > 0;
      if (placement && hasAspect) {
        const fitted = fitFrameToAspect(placement.frame, aspect, placement.zone);
        payload.width = fitted.width;
        payload.height = fitted.height;
        payload.left = fitted.left;
        payload.top = fitted.top;
      } else {
        console.warn(`[DesignHuddleUploadOverride] frame not re-fitted for element ${elementId} (frame ${placement ? 'read' : 'unavailable'}, aspect ${hasAspect ? aspect : 'unknown'}) — url-only update; the export guard will flag a distorted frame.`);
      }

      editor.updateElements({
        elements: {
          [elementId]: payload
        }
      });
      return;
    }

    editor.addElement({
      element_classes: ['image'],
      type: 'image',
      placement: data?.context === 'background' ? 'background' : 'foreground',
      url,
      selected: false
    }, (err: any) => {
      if (err) {
        console.error('[DesignHuddleUploadOverride] addElement failed:', err);
        this.notification.error('Could not add the uploaded image to the design.', 'Upload failed');
      }
    });
  }

  /**
   * Current frame (and zone, when DH reports one) of an element in the live editor, via the SDK
   * instance's getProjectData. Resolves null — never rejects — on error, timeout, or when the
   * element cannot be found, so applyToEditor can always fall back to a url-only update.
   */
  private readElementFrame(editor: any, elementId: string): Promise<{ frame: Frame; zone: Frame | null } | null> {
    if (!editor || typeof editor.getProjectData !== 'function') {
      return Promise.resolve(null);
    }

    return new Promise(resolve => {
      let settled = false;
      const finish = (value: { frame: Frame; zone: Frame | null } | null) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };
      const timer = setTimeout(() => finish(null), PROJECT_DATA_TIMEOUT_MS);

      try {
        editor.getProjectData({ simple: false }, (err: any, projectData: any) => {
          if (err) {
            console.warn('[DesignHuddleUploadOverride] getProjectData failed:', err);
            finish(null);
            return;
          }
          finish(DesignHuddleUploadOverrideService.findElementFrame(projectData, elementId));
        });
      } catch (e) {
        console.warn('[DesignHuddleUploadOverride] getProjectData threw:', e);
        finish(null);
      }
    });
  }

  /**
   * Locates `elementId` in a DH project-data payload (`pages[].elements` — an object keyed by id,
   * or an array) and returns its frame + zone in DH's inch space. Same shape parseProjectMetadata
   * reads in product-color-selection.component.ts.
   */
  static findElementFrame(projectData: any, elementId: string): { frame: Frame; zone: Frame | null } | null {
    const root = projectData?.pages ? projectData : (projectData?.data || projectData);
    const pages: any[] = root?.pages || root?.scenes || [];
    for (const page of pages) {
      const elements = page?.elements;
      if (!elements) continue;
      const list: any[] = Array.isArray(elements)
        ? elements
        : Object.keys(elements).map(key => ({ element_id: key, ...elements[key] }));
      const element = list.find(e => String(e?.element_id ?? e?.id) === String(elementId));
      if (!element) continue;

      const frame = DesignHuddleUploadOverrideService.toFrame(element);
      if (!frame) return null;
      const zone = element.zone ? DesignHuddleUploadOverrideService.toFrame(element.zone) : null;
      return { frame, zone };
    }
    return null;
  }

  private static toFrame(source: any): Frame | null {
    const width = Number(source?.width);
    const height = Number(source?.height);
    const left = Number(source?.left ?? 0);
    const top = Number(source?.top ?? 0);
    if (!(width > 0) || !(height > 0) || !Number.isFinite(left) || !Number.isFinite(top)) return null;
    return { width, height, left, top };
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('File read failed'));
      reader.readAsDataURL(file);
    });
  }

  // btoa() only handles Latin1 — this round-trip through encodeURIComponent lets it safely
  // base64-encode arbitrary UTF-8 SVG markup (e.g. a designer's name in a <title>/<desc>).
  private utf8ToBase64(text: string): string {
    return btoa(unescape(encodeURIComponent(text)));
  }
}
